// The header's "more actions" control (KAN-773 / RD-UI-066): a horizontal ⋯ as a
// 44px plain icon button, the SAME look as the ✕ it sits beside (no box), that
// opens the app's ActionMenu (bottom action sheet when `isMobile`). Opt-in via
// FullScreenFlowHeader's `moreMenu` prop; renders nothing without items.
import { useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { useIntl } from 'react-intl';
import ActionMenu from '../ActionMenu';
import { foundation } from '../../../ui';

const fc = foundation.color;

const button = {
  width: 44,
  height: 44,
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: 'none',
  background: 'transparent',
  borderRadius: foundation.radii.md,
  color: fc.neutral.textSecondary,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
};
// Pressed/open state, as in the approved mockup (the menu is open from this button).
const buttonOpen = { background: fc.neutral.surfaceMuted, color: fc.neutral.text };

/**
 * @param {object} props
 * @param {Array} props.items - ActionMenu items ({ key, label, icon?, tone?, disabled?, tooltip?, onClick }).
 * @param {boolean} [props.isMobile] - render the menu as the bottom action sheet.
 * @param {string} [props.ariaLabel] - defaults to "More actions".
 * @param {string} [props.testId] - the ⋯ button's test id.
 * @param {string} [props.testIdPrefix] - ActionMenu item test id prefix.
 * @param {(open: boolean) => void} [props.onOpenChange] - lets the host gate its own Escape handler
 *   while the menu is open (one Escape closes only the menu).
 */
export default function FullScreenHeaderMoreMenu({
  items, isMobile = false, ariaLabel, testId = 'fullscreen-flow-header-more', testIdPrefix = 'fullscreen-flow-header-more-item', onOpenChange,
}) {
  const intl = useIntl();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { onOpenChange?.(open); }, [open, onOpenChange]);
  if (!items || items.length === 0) return null;
  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        style={open ? { ...button, ...buttonOpen } : button}
        aria-label={ariaLabel || intl.formatMessage({ id: 'common.moreActions', defaultMessage: 'More actions' })}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
      >
        <MoreHorizontal size={20} strokeWidth={2} aria-hidden />
      </button>
      <ActionMenu
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchorRef}
        items={items}
        isMobile={isMobile}
        testIdPrefix={testIdPrefix}
      />
    </>
  );
}
